import React, { useEffect, useState } from 'react';
import axios from 'axios';
import { X, Euro, BarChart3, Loader2 } from 'lucide-react';
import { toast } from 'sonner';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const fmtDT = (v) => {
  if (!v) return '—';
  try { return new Date(v).toLocaleDateString('el-GR', { day: '2-digit', month: '2-digit', year: 'numeric' }); } catch { return String(v); }
};

const REASON_LABELS = {
  signup_bonus: 'Μπόνους εγγραφής', entry_create: 'Νέα καταχώρηση', entry_create_batch: 'Καταχωρήσεις (μαζικά)',
  ai_week_plan: 'AI Organiser', admin_adjust: 'Ρύθμιση από admin', postponement: 'Αναβολή',
  financial_entry_create: 'Οικονομική εγγραφή', limited_plan_monthly_credits: 'Μηνιαίες μονάδες πακέτου',
};

export const UserOverviewModal = ({ userId, onClose }) => {
  const [data, setData] = useState(null);
  useEffect(() => {
    if (!userId) return;
    setData(null);
    axios.get(`${API}/admin/users/${userId}/overview`, { withCredentials: true })
      .then((r) => setData(r.data))
      .catch(() => { toast.error('Αποτυχία φόρτωσης καρτέλας'); onClose(); });
  }, [userId]);
  if (!userId) return null;
  return (
    <div className="fixed inset-0 z-[80] flex items-center justify-center bg-slate-900/40 p-4" data-testid="admin-user-overview">
      <div className="w-full max-w-2xl bg-white rounded-2xl shadow-2xl border border-stone-200 max-h-[85vh] overflow-y-auto">
        <div className="sticky top-0 bg-white border-b border-stone-200 px-5 py-3 flex items-center justify-between rounded-t-2xl">
          <h3 className="text-base font-semibold text-slate-900">Καρτέλα χρήστη</h3>
          <button onClick={onClose} data-testid="user-overview-close" className="w-7 h-7 flex items-center justify-center text-slate-400 hover:text-slate-700"><X className="w-4 h-4" /></button>
        </div>
        {!data ? (
          <div className="p-10 flex justify-center"><Loader2 className="w-6 h-6 animate-spin text-slate-400" /></div>
        ) : (
          <div className="p-5 space-y-4">
            <div>
              <p className="text-lg font-semibold text-slate-900">{data.user.name || '—'}</p>
              <p className="text-sm text-slate-600">{data.user.email} {data.user.office_name ? `· ${data.user.office_name}` : ''}</p>
              <p className="text-xs text-slate-500 mt-1">Εγγραφή: {fmtDT(data.user.created_at)} · Πακέτο: {data.user.subscription_plan || 'free'}{data.user.subscription_end ? ` (έως ${fmtDT(data.user.subscription_end)})` : ''}</p>
            </div>
            <div className="grid grid-cols-3 gap-2">
              {[['Μονάδες', data.user.credits ?? 0], ['Σύνολο δαπανών', data.credits_spent_total], ['Σύνολο χορηγήσεων', data.credits_granted_total]].map(([l, v]) => (
                <div key={l} className="bg-amber-50/60 border border-amber-200 rounded-lg p-2.5 text-center">
                  <p className="text-lg font-bold text-slate-900">{v}</p>
                  <p className="text-[11px] text-slate-600">{l}</p>
                </div>
              ))}
            </div>
            <div className="grid grid-cols-5 gap-2">
              {[['Πελάτες', data.counts.clients], ['Υποθέσεις', data.counts.cases], ['Εγγραφές', data.counts.entries], ['Εργασίες', data.counts.day_tasks], ['Οικονομικές', data.counts.financial_entries]].map(([l, v]) => (
                <div key={l} className="bg-white border border-stone-200 rounded-lg p-2 text-center">
                  <p className="text-base font-semibold text-slate-900">{v}</p>
                  <p className="text-[10px] text-slate-500">{l}</p>
                </div>
              ))}
            </div>
            <div>
              <p className="text-sm font-semibold text-slate-800 mb-2">Ιστορικό μονάδων (τελευταίες 30)</p>
              <div className="border border-stone-200 rounded-lg divide-y divide-stone-100 max-h-56 overflow-y-auto">
                {data.transactions.length === 0 && <p className="p-3 text-sm text-slate-500">Καμία συναλλαγή</p>}
                {data.transactions.map((t, i) => (
                  <div key={i} className="flex items-center justify-between px-3 py-1.5 text-xs">
                    <span className="text-slate-600">{fmtDT(t.created_at)} · {REASON_LABELS[t.reason] || t.reason}</span>
                    <span className={`font-mono font-semibold ${t.amount < 0 ? 'text-red-700' : 'text-green-700'}`}>{t.amount > 0 ? `+${t.amount}` : t.amount}</span>
                  </div>
                ))}
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export const FinancePanel = () => {
  const [data, setData] = useState(null);
  useEffect(() => {
    axios.get(`${API}/admin/finance/overview`, { withCredentials: true }).then((r) => setData(r.data)).catch(() => {});
  }, []);
  if (!data) return null;
  return (
    <div className="bg-white rounded-xl border border-stone-200 overflow-hidden" data-testid="admin-finance-panel">
      <div className="px-4 py-3 border-b border-stone-200 flex items-center gap-2">
        <Euro className="w-4 h-4 text-red-700" />
        <h2 className="text-sm font-semibold text-slate-900">Οικονομική εικόνα</h2>
        <span className="ml-auto text-xs text-slate-500">Ενεργές συνδρομές: {data.active_unlimited} unlimited (ARR €{data.unlimited_arr_eur}) · {data.active_limited} limited</span>
      </div>
      <div className="overflow-x-auto">
        <table className="w-full text-sm">
          <thead className="bg-stone-50 text-slate-600 text-xs uppercase tracking-wider">
            <tr>
              <th className="px-3 py-2 text-left">Μήνας</th>
              <th className="px-3 py-2 text-center">Μονάδες που καταναλώθηκαν</th>
              <th className="px-3 py-2 text-center">Μονάδες που χορηγήθηκαν</th>
              <th className="px-3 py-2 text-center">Αγορές (€)</th>
              <th className="px-3 py-2 text-center">Συναλλαγές</th>
            </tr>
          </thead>
          <tbody className="divide-y divide-stone-100">
            {data.months.length === 0 && <tr><td colSpan={5} className="px-3 py-4 text-center text-slate-500">Χωρίς δεδομένα</td></tr>}
            {data.months.map((m) => (
              <tr key={m.month}>
                <td className="px-3 py-2 font-medium text-slate-900">{m.month}</td>
                <td className="px-3 py-2 text-center font-mono text-red-700">{m.credits_spent}</td>
                <td className="px-3 py-2 text-center font-mono text-green-700">{m.credits_granted}</td>
                <td className="px-3 py-2 text-center font-mono">{m.purchases_eur.toFixed(2)}</td>
                <td className="px-3 py-2 text-center text-slate-600">{m.tx_count}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
};

export const UsagePanel = () => {
  const [month, setMonth] = useState(new Date().toISOString().slice(0, 7));
  const [data, setData] = useState(null);
  useEffect(() => {
    axios.get(`${API}/admin/usage/monthly`, { params: { month }, withCredentials: true }).then((r) => setData(r.data)).catch(() => {});
  }, [month]);
  return (
    <div className="bg-white rounded-xl border border-stone-200 overflow-hidden" data-testid="admin-usage-panel">
      <div className="px-4 py-3 border-b border-stone-200 flex items-center gap-2">
        <BarChart3 className="w-4 h-4 text-amber-700" />
        <h2 className="text-sm font-semibold text-slate-900">Χρήση ανά χρήστη</h2>
        <input type="month" value={month} onChange={(e) => setMonth(e.target.value)} data-testid="usage-month-input"
          className="ml-auto h-8 px-2 border border-stone-300 rounded-lg text-sm bg-white" />
      </div>
      <div className="overflow-x-auto">
        <table className="w-full text-sm">
          <thead className="bg-stone-50 text-slate-600 text-xs uppercase tracking-wider">
            <tr>
              <th className="px-3 py-2 text-left">Χρήστης</th>
              <th className="px-3 py-2 text-center">Καταχωρήσεις</th>
              <th className="px-3 py-2 text-center">AI Organiser</th>
              <th className="px-3 py-2 text-center">Μονάδες</th>
            </tr>
          </thead>
          <tbody className="divide-y divide-stone-100">
            {(!data || data.rows.length === 0) && <tr><td colSpan={4} className="px-3 py-4 text-center text-slate-500">Χωρίς χρήση τον μήνα αυτόν</td></tr>}
            {data && data.rows.map((r) => (
              <tr key={r.user_id}>
                <td className="px-3 py-2"><span className="font-medium text-slate-900">{r.name || '—'}</span> <span className="text-xs text-slate-500">{r.email}</span></td>
                <td className="px-3 py-2 text-center font-mono">{r.entries}</td>
                <td className="px-3 py-2 text-center font-mono">{r.organiser_uses}</td>
                <td className="px-3 py-2 text-center font-mono text-red-700">{r.credits_spent}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
};
